import { useState, useEffect, useCallback, useRef } from "react";
import { useWebSocket } from "contexts/WebSocketContext";
import { useAuth } from "contexts/AuthContext";
import { useOutletContext } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";

import { fakeToken } from "config";
import dataSourceConfig from "dataSource";
import store from "stores";

import { getValueByPath } from "utils/getValueByPath";
import { sendWsDebugMessage } from "utils/wsDebugger";

function interPlaceholders(resource, restfulPlaceholders) {
  return resource.replace(/:([a-zA-Z0-9_]+)/g, (_, key) => {
    if (!(key in restfulPlaceholders)) {
      console.warn(
        `Missing restfulPlaceholders for key "${key}" in resource config: "${resource}"`,
      );
      return "";
    }
    return restfulPlaceholders[key];
  });
}

const getSupportSession = () => {
  try {
    const supportSession = sessionStorage.getItem("supportSession");

    return supportSession ? JSON.parse(supportSession) : null;
  } catch (error) {
    console.error("Failed to read supportSession:", error);

    return null;
  }
};

const useDataSourceManager = (layoutWsData) => {
  const checkMockup = process.env.REACT_APP_IS_MOCKUP;
  const ws = useWebSocket();
  const outletContext = useOutletContext();
  const wsData = outletContext ? outletContext.wsData : layoutWsData;

  const { token } = useAuth();
  const [decodeTokenData, setDecodeTokenData] = useState(null);

  const dispatch = useDispatch();
  const reduxState = useSelector((state) => state);

  const [dataMap, setDataMap] = useState({});
  const lastProcessedIndex = useRef(-1);

  const getData = useCallback(
    (uuid) => {
      // Extract data and remove it from the map, then return it
      const data = dataMap[uuid];

      if (data) {
        setDataMap((prev) => {
          const newDataMap = { ...prev };
          delete newDataMap[uuid];
          return newDataMap;
        });
        return data;
      }

      return null;
    },
    [dataMap],
  );

  const [currentRequestUuid, setCurrentRequestUuid] = useState(null);
  const [currentSelector, setCurrentSelector] = useState(null);

  // 暫時儲存 sendBatch 狀態
  const pendingSendBatchResolvers = useRef({});
  const activeSendBatchRequests = useRef(new Set());

  useEffect(() => {
    if (checkMockup === "false" && token && !decodeTokenData) {
      const decodedToken = jwtDecode(token);
      setDecodeTokenData(decodedToken);
    }
    if (checkMockup === "true" && !decodeTokenData) {
      const decodedToken = jwtDecode(fakeToken);
      setDecodeTokenData(decodedToken);
    }
  }, []);

  const processWsMessage = useCallback((msg) => {
    const uuid = msg?.uuid || msg?.id;
    if (!uuid) return;

    // Support both the app's wrapped WebSocket payload and a raw JSON-RPC response.
    const payload = msg.response || msg.error || msg;
    const result = payload?.result ?? payload;

    // 處理 sendBatch
    if (pendingSendBatchResolvers.current[uuid]) {
      pendingSendBatchResolvers.current[uuid](result);

      delete pendingSendBatchResolvers.current[uuid];
      activeSendBatchRequests.current.delete(uuid);

      setDataMap((prev) => {
        if (!(uuid in prev)) return prev;
        const newData = { ...prev };
        delete newData[uuid];
        return newData;
      });
    } else {
      setDataMap((prev) => ({
        ...prev,
        [uuid]: result,
      }));
    }
  }, []);

  useEffect(() => {
    if (Array.isArray(wsData)) {
      if (wsData.length === 0) {
        lastProcessedIndex.current = -1;
        return;
      }
      for (let i = lastProcessedIndex.current + 1; i < wsData.length; i++) {
        processWsMessage(wsData[i]);
        lastProcessedIndex.current = i;
      }
    } else if (wsData && (wsData.uuid || wsData.id)) {
      processWsMessage(wsData);
    }
  }, [wsData, processWsMessage]);

  useEffect(() => {
    if (currentRequestUuid && currentSelector) {
      setDataMap((pre) => ({
        ...pre,
        [currentRequestUuid]: getValueByPath(reduxState, currentSelector),
      }));
      setCurrentRequestUuid(null);
      setCurrentSelector(null);
    }
  }, [currentRequestUuid, currentSelector, reduxState]);

  const sendRequest = useCallback(
    async (apiKey, requestUuid, restfulPlaceholders = null, params = {}) => {
      const config = dataSourceConfig[apiKey];

      if (!config) {
        throw new Error(`API config not found for key: ${apiKey}`);
      }

    const { protocol, method, restful, resource, buildParams, action, mockSelector, mockHandler } = config;

          const supportSession = getSupportSession();

      const effectiveRestfulPlaceholders = {
        ...(restfulPlaceholders || {}),
      };

      if (supportSession?.orgId) {
        effectiveRestfulPlaceholders.orgId = supportSession.orgId;
      }
    if (checkMockup === "true") {
      const mockupData = mockHandler
        ? mockHandler({ state: reduxState, restfulPlaceholders, params })
        : getValueByPath(reduxState, mockSelector);

        if (requestUuid === null) {
          return mockupData;
        }
        if (action) {
          const formattedParams = restful
            ? { ...effectiveRestfulPlaceholders, ...params }
            : params;
          dispatch(action(formattedParams));
          // Read fresh store state after the action so concurrent mock requests
          // (e.g. liveView detail per id) each get their own dataMap entry.
          const freshData = mockHandler
            ? mockHandler({
                state: store.getState(),
                restfulPlaceholders: effectiveRestfulPlaceholders,
                params,
              })
            : getValueByPath(store.getState(), mockSelector);
          setDataMap((pre) => ({ ...pre, [requestUuid]: freshData }));

          // Fix: Ensure sendBatch resolves even if an action is dispatched in mockup mode
          if (pendingSendBatchResolvers.current[requestUuid]) {
            return pendingSendBatchResolvers.current[requestUuid](freshData);
          }
          return;
        }
        // 處理 sendBatch
        if (pendingSendBatchResolvers.current[requestUuid])
          return pendingSendBatchResolvers.current[requestUuid](mockupData);

        setDataMap((pre) => ({ ...pre, [requestUuid]: mockupData }));
      }

      if (checkMockup === "false" && protocol === "ws") {
        const currentResource = restful
          ? interPlaceholders(resource, effectiveRestfulPlaceholders)
          : resource;
        const requests = {
          jsonrpc: "2.0",
          id: requestUuid,
          method,
          params: {
            resource: currentResource,
            accessToken: token,
            ...(buildParams ? buildParams(params) : params),
          },
        };
        sendWsDebugMessage("request", requests);
        ws.send(JSON.stringify(requests));
      }

      if (checkMockup === "false" && protocol === "https") {
        if (method === "casdoor") {
          const result = await config.handler(
            config.service,
            params,
            config.token ? token : null,
          );
          return result;
        }
      }
    },
    [ws, token, reduxState],
  );

  const sendBatch = useCallback(
    async (requests) => {
      const results = [];
      const sendBatchDataMap = {};

      for (const {
        apiKey,
        requestUuid,
        restfulPlaceholders,
        params,
      } of requests) {
        const result = await new Promise((resolve) => {
          // 設定 5 分鐘 如果 api 沒回應就會斷掉
          const timeoutId = setTimeout(
            () => {
              // 超時後清理 resolver 和 active request
              delete pendingSendBatchResolvers.current[requestUuid];
              activeSendBatchRequests.current.delete(requestUuid);
              resolve({ error: "timeout", requestUuid });
            },
            5 * 60 * 1000,
          );

          pendingSendBatchResolvers.current[requestUuid] = (res) => {
            clearTimeout(timeoutId);
            resolve(res);
          };

          activeSendBatchRequests.current.add(requestUuid);
          sendRequest(apiKey, requestUuid, restfulPlaceholders, params);
        });

        results.push(result);
        sendBatchDataMap[requestUuid] = result;
      }

      const getSendBatchData = (params) =>
        sendBatchDataMap[params?.requestUuid];

      return { results, sendBatchDataMap, getSendBatchData };
    },
    [sendRequest],
  );

  return {
    dataMap,
    getData,
    decodeTokenData,
    sendRequest,
    sendBatch,
  };
};

export default useDataSourceManager;
